import { RouteObject } from 'react-router-dom';

import { Login } from 'features/account/Login';
import { ActivateAccount } from 'features/activate-account/ActivateAccount';
import { ActivateAccountError } from 'features/activate-account/ActivateAccountError';
import { ActivateAccountSuccess } from 'features/activate-account/ActivateAccountSuccess';
import { CampaignPreview } from 'features/campaign/CampaignPreview';
import { Campaigns } from 'features/campaign/Campaigns';
import { NewCampaign } from 'features/campaign/NewCampaign';
import { CheckoutSuccess, ItemCheckout } from 'features/checkout';
import { OnSiteCheckout } from 'features/checkout/onsite/OnSiteCheckout';
import { ConfirmEmail } from 'features/confirm-email/ConfirmEmail';
import { ConfirmEmailError } from 'features/confirm-email/ConfirmEmailError';
import { ConfirmEmailSuccess } from 'features/confirm-email/ConfirmEmailSuccess';
import { PageNotFound } from 'features/error/PageNotFound';
import { FundraiserPagePreview } from 'features/fundraiser/FundraiserPagePreview';
import { Home } from 'features/home/Home';
import { HotDeals } from 'features/item/HotDeals';
import { ItemPreview } from 'features/item/ItemPreview';
import { NewItem } from 'features/item/NewItem';
import { Messenger } from 'features/messenger/Messenger';
import { RedirectLegacyMessengerLink } from 'features/messenger/RedirectLegacyMessengerLink';
import { AccountSettingsPage } from 'features/my-profile/account-settings/AccountSettingsPage';
import { LocationPage } from 'features/my-profile/location/LocationPage';
import { MyActivityBoughtPage } from 'features/my-profile/my-activity/MyActivityBought';
import { MyActivityCampaignsPage } from 'features/my-profile/my-activity/MyActivityCampaignsPage';
import { MyActivityDonatedPage } from 'features/my-profile/my-activity/MyActivityDonatedPage';
import { MyActivityPage } from 'features/my-profile/my-activity/MyActivityPage';
import { MyActivitySellingPage } from 'features/my-profile/my-activity/MyActivitySellingPage';
import { MyActivityTrackedItemPage } from 'features/my-profile/my-activity/MyActivityTrackedItemPage';
import { MyProfile } from 'features/my-profile/MyProfile';
import { NotificationsPage } from 'features/my-profile/notifications/NotificationsPage';
import { PasswordPage } from 'features/my-profile/password/PasswordPage';
import { PaymentPage } from 'features/my-profile/payment/PaymentPage';
import { ShippingAddressPage } from 'features/my-profile/shipping-address/ShippingAddressPage';
import { ResetPassword } from 'features/reset-password/ResetPassword';
import { ResetPasswordError } from 'features/reset-password/ResetPasswordError';
import { ResetPasswordSuccess } from 'features/reset-password/ResetPasswordSuccess';
import { UnsubscribeEmail } from 'features/user/UnsubscribeEmail';
import { MyProfileWrapper } from 'navigation/wrapper/layout/MyProfileWrapper';
import { RootWrapper } from 'navigation/wrapper/RootWrapper';
import { DefaultScreenWrapper } from 'navigation/wrapper/screen/DefaultScreenWrapper';
import { LogoAndReturnOnlyScreenWrapper } from 'navigation/wrapper/screen/LogoAndReturnOnlyScreenWrapper';
import { LogoOnlyScreenWrapper } from 'navigation/wrapper/screen/LogoOnlyScreenWrapper';
import { NoFooterScreenWrapper } from 'navigation/wrapper/screen/NoFooterScreenWrapper';
import { ScreenWrapper } from 'navigation/wrapper/screen/ScreenWrapper';
import { PrivateRouteWrapper } from 'navigation/wrapper/security/PrivateRouteWrapper';

import { ROUTE_CAMPAIGNS, ROUTE_FUNDRAISERS, ROUTE_HOME, ROUTE_ITEMS, ROUTE_ONSITE_CHECKOUT } from './routePaths';

const screenConfig: RouteObject[] = [
  {
    element: <DefaultScreenWrapper />,
    children: [
      { path: ROUTE_HOME, element: <Home /> },
      {
        path: ROUTE_CAMPAIGNS,
        children: [
          { path: '', element: <Campaigns /> },
          { path: ':campaignName/:campaignId', element: <CampaignPreview /> },
          { path: ':campaignId', element: <CampaignPreview /> },
        ],
      },
      {
        path: ROUTE_ITEMS,
        children: [
          { path: '', element: <HotDeals /> },
          { path: ':itemName/:itemId', element: <ItemPreview /> },
          { path: ':itemId', element: <ItemPreview /> },
        ],
      },
      { path: '/new-campaign', element: <NewCampaign /> },
      { path: '/new-item', element: <NewItem /> },
      { path: `${ROUTE_FUNDRAISERS}/:subdomainName`, element: <FundraiserPagePreview /> },
      { path: '/messages/from/:senderId', element: <RedirectLegacyMessengerLink /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/reset-password-success', element: <ResetPasswordSuccess /> },
      { path: '/reset-password-error', element: <ResetPasswordError /> },
      { path: '/activate-account-success', element: <ActivateAccountSuccess /> },
      { path: '/activate-account-error', element: <ActivateAccountError /> },
      { path: '/activate-account', element: <ActivateAccount /> },
      { path: '/confirm-email-success', element: <ConfirmEmailSuccess /> },
      { path: '/confirm-email-error', element: <ConfirmEmailError /> },
      { path: '/confirm-email', element: <ConfirmEmail /> },
      { path: '/unsubscribe', element: <UnsubscribeEmail /> },
      { path: '/login', element: <Login /> },
      {
        path: '/my-profile',
        element: <PrivateRouteWrapper />,
        children: [
          {
            element: <MyProfileWrapper />,
            children: [
              { path: '', element: <MyProfile /> },
              { path: 'my-activity', element: <MyActivityPage /> },
              { path: 'my-activity/campaigns', element: <MyActivityCampaignsPage /> },
              { path: 'my-activity/selling', element: <MyActivitySellingPage /> },
              { path: 'my-activity/donated', element: <MyActivityDonatedPage /> },
              { path: 'my-activity/bought', element: <MyActivityBoughtPage /> },
              { path: 'shipping-address', element: <ShippingAddressPage /> },
              { path: 'payment', element: <PaymentPage /> },
              { path: 'location', element: <LocationPage /> },
              { path: 'password', element: <PasswordPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'account-settings', element: <AccountSettingsPage /> },
            ],
          },
          { path: 'my-activity/donated/:itemId/', element: <MyActivityTrackedItemPage /> },
          { path: 'my-activity/bought/:itemId/:orderId', element: <MyActivityTrackedItemPage /> },
        ],
      },
    ],
  },
  {
    path: '/checkout',
    element: <PrivateRouteWrapper />,
    children: [
      {
        element: <LogoOnlyScreenWrapper />,
        children: [{ path: ':itemId', element: <ItemCheckout payForOffer={false} /> }],
      },
      {
        element: <LogoOnlyScreenWrapper />,
        children: [{ path: ':itemId/offer', element: <ItemCheckout payForOffer={true} /> }],
      },
      {
        element: <LogoAndReturnOnlyScreenWrapper />,
        children: [{ path: 'success', element: <CheckoutSuccess /> }],
      },
    ],
  },
  {
    path: ROUTE_ONSITE_CHECKOUT,
    element: <LogoOnlyScreenWrapper />,
    children: [{ path: ':data', element: <OnSiteCheckout /> }],
  },
  {
    path: '/messenger',
    element: <NoFooterScreenWrapper />,
    children: [
      {
        element: <PrivateRouteWrapper />,
        children: [
          { path: '', element: <Messenger /> },
          { path: 'user/:userId/item/:itemId', element: <Messenger /> },
          { path: 'user/:userId/campaign/:campaignId', element: <Messenger /> },
          { path: 'user/:userId', element: <Messenger /> },
          { path: 'support', element: <Messenger /> },
          { path: ':conversationId', element: <Messenger /> },
        ],
      },
    ],
  },
  {
    element: <DefaultScreenWrapper />,
    children: [{ path: '*', element: <PageNotFound /> }],
  },
];

export const routesConfig: RouteObject[] = [
  {
    element: <RootWrapper />,
    children: [{ element: <ScreenWrapper />, children: screenConfig }],
  },
];
